<!--
  Controller Read-Only View - Show Attached Equipment

  FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/ControllerReadOnlyView.vue

  Displays equipment attached to controller in a folder structure
  Allows viewing (but not editing) attached equipment's SAX programs
-->

<template>
  <div class="controller-readonly-view">
    <q-card flat bordered class="full-height">
      <q-toolbar class="bg-blue-grey-1">
        <q-icon name="folder_special" class="q-mr-sm" />
        <q-toolbar-title class="text-subtitle2">
          Attached Equipment
        </q-toolbar-title>

        <q-btn
          icon="refresh"
          flat
          dense
          @click="loadAttachedEquipment"
        >
          <q-tooltip>Refresh</q-tooltip>
        </q-btn>
      </q-toolbar>

      <q-separator />

      <!-- Loading State -->
      <div v-if="isLoading" class="q-pa-md text-center">
        <q-spinner color="primary" size="3em" />
        <div class="text-caption text-grey-7 q-mt-sm">Loading equipment...</div>
      </div>

      <!-- Empty State -->
      <div v-else-if="attachedEquipment.length === 0" class="q-pa-md text-center">
        <q-icon name="devices_other" size="4em" color="grey-5" />
        <div class="text-caption text-grey-7 q-mt-sm">
          No equipment attached to this controller
        </div>
      </div>

      <!-- Equipment Tree -->
      <div v-else class="equipment-tree">
        <q-splitter v-model="splitterModel" :limits="[30, 70]">
          <!-- Left Panel: Tree -->
          <template #before>
            <q-scroll-area class="fit">
              <div class="q-pa-sm">
                <!-- Search -->
                <q-input
                  v-model="searchQuery"
                  dense
                  outlined
                  placeholder="Search equipment..."
                  class="q-mb-sm"
                  clearable
                >
                  <template #prepend>
                    <q-icon name="search" />
                  </template>
                </q-input>

                <!-- Tree -->
                <q-tree
                  :nodes="equipmentTree"
                  node-key="id"
                  :filter="searchQuery"
                  :filter-method="filterEquipment"
                  default-expand-all
                  @update:selected="handleEquipmentSelect"
                >
                  <template #default-header="prop">
                    <div
                      class="row items-center full-width"
                      :class="{ 'selected-node': selectedEquipmentId === prop.node.equipment_id }"
                      @click="selectEquipment(prop.node)"
                    >
                      <q-icon
                        :name="prop.node.icon"
                        :color="prop.node.color"
                        size="sm"
                        class="q-mr-sm"
                      />

                      <div class="col">
                        <div class="text-weight-medium">{{ prop.node.label }}</div>
                        <div v-if="prop.node.subtitle" class="text-caption text-grey-7">
                          {{ prop.node.subtitle }}
                        </div>
                      </div>

                      <q-badge
                        v-if="prop.node.has_sax"
                        color="primary"
                        rounded
                      >
                        SAX
                        <q-tooltip>Has SAX program</q-tooltip>
                      </q-badge>
                    </div>
                  </template>
                </q-tree>
              </div>
            </q-scroll-area>
          </template>

          <!-- Right Panel: Details -->
          <template #after>
            <q-scroll-area class="fit">
              <div class="q-pa-md">
                <!-- No Selection -->
                <div v-if="!selectedEquipment" class="text-center q-pa-xl text-grey-6">
                  <q-icon name="touch_app" size="4em" />
                  <div class="text-h6 q-mt-md">Select equipment to view details</div>
                </div>

                <!-- Selected Equipment -->
                <div v-else>
                  <!-- Equipment Info -->
                  <q-card flat bordered class="q-mb-md">
                    <q-card-section>
                      <div class="row items-center">
                        <q-icon
                          :name="getEquipmentIcon(selectedEquipment.type)"
                          :color="getEquipmentColor(selectedEquipment.type)"
                          size="md"
                          class="q-mr-md"
                        />

                        <div class="col">
                          <div class="text-h6">{{ selectedEquipment.name }}</div>
                          <div class="text-caption text-grey-7">
                            {{ selectedEquipment.type?.toUpperCase() || 'Unknown' }}
                          </div>
                        </div>

                        <q-btn
                          icon="open_in_new"
                          flat
                          dense
                          color="primary"
                          @click="openEquipmentEditor"
                        >
                          <q-tooltip>Open in editor</q-tooltip>
                        </q-btn>
                      </div>

                      <q-separator class="q-my-md" />

                      <div class="row q-gutter-sm">
                        <div class="col">
                          <div class="text-caption text-grey-7">Location</div>
                          <div>{{ selectedEquipment.location || 'Not specified' }}</div>
                        </div>

                        <div class="col">
                          <div class="text-caption text-grey-7">Status</div>
                          <q-badge
                            :color="selectedEquipment.status === 'active' ? 'positive' : 'grey'"
                          >
                            {{ selectedEquipment.status || 'Unknown' }}
                          </q-badge>
                        </div>
                      </div>
                    </q-card-section>
                  </q-card>

                  <!-- SAX Program Info -->
                  <q-card v-if="selectedEquipmentSaxData" flat bordered class="q-mb-md">
                    <q-card-section>
                      <div class="text-subtitle2 q-mb-sm">SAX Program</div>

                      <div class="row q-gutter-sm">
                        <q-card flat bordered class="col">
                          <q-card-section class="q-pa-sm">
                            <div class="text-caption text-grey-7">Components</div>
                            <div class="text-h6">
                              {{ selectedEquipmentSaxData.components?.length || 0 }}
                            </div>
                          </q-card-section>
                        </q-card>

                        <q-card flat bordered class="col">
                          <q-card-section class="q-pa-sm">
                            <div class="text-caption text-grey-7">Links</div>
                            <div class="text-h6">
                              {{ selectedEquipmentSaxData.links?.length || 0 }}
                            </div>
                          </q-card-section>
                        </q-card>

                        <q-card flat bordered class="col">
                          <q-card-section class="q-pa-sm">
                            <div class="text-caption text-grey-7">Kits</div>
                            <div class="text-h6">
                              {{ uniqueKits.length }}
                            </div>
                          </q-card-section>
                        </q-card>
                      </div>

                      <q-separator class="q-my-md" />

                      <div class="row q-gutter-sm">
                        <q-btn
                          icon="visibility"
                          label="View Canvas"
                          color="primary"
                          outline
                          size="sm"
                          @click="showCanvasPreview = true"
                        />

                        <q-btn
                          icon="download"
                          label="Export SAX"
                          color="secondary"
                          outline
                          size="sm"
                          @click="exportSaxForEquipment"
                        />
                      </div>
                    </q-card-section>
                  </q-card>

                  <!-- No SAX Program -->
                  <q-banner v-else class="bg-grey-2" rounded>
                    <template #avatar>
                      <q-icon name="info" color="grey" />
                    </template>
                    This equipment does not have a SAX program
                  </q-banner>

                  <!-- Components List -->
                  <q-card
                    v-if="selectedEquipmentSaxData?.components?.length > 0"
                    flat
                    bordered
                    class="q-mb-md"
                  >
                    <q-card-section>
                      <div class="text-subtitle2 q-mb-sm">Components</div>

                      <q-list bordered separator>
                        <q-item
                          v-for="component in selectedEquipmentSaxData.components"
                          :key="component.id"
                          dense
                        >
                          <q-item-section avatar>
                            <div
                              class="color-preview"
                              :style="{ backgroundColor: component.color || '#3B82F6' }"
                            ></div>
                          </q-item-section>

                          <q-item-section>
                            <q-item-label>{{ component.name }}</q-item-label>
                            <q-item-label caption>
                              {{ component.type }} ({{ component.kit_name || 'sedona' }})
                            </q-item-label>
                          </q-item-section>

                          <q-item-section side>
                            <div class="text-caption text-grey-7">
                              <q-icon name="input" size="xs" color="green" />
                              {{ component.input_slots?.length || 0 }}
                              <q-icon name="output" size="xs" color="orange" class="q-ml-xs" />
                              {{ component.output_slots?.length || 0 }}
                            </div>
                          </q-item-section>
                        </q-item>
                      </q-list>
                    </q-card-section>
                  </q-card>

                  <!-- Links List -->
                  <q-card
                    v-if="selectedEquipmentSaxData?.links?.length > 0"
                    flat
                    bordered
                  >
                    <q-card-section>
                      <div class="text-subtitle2 q-mb-sm">Links</div>

                      <q-list bordered separator>
                        <q-item
                          v-for="link in selectedEquipmentSaxData.links"
                          :key="link.id"
                          dense
                        >
                          <q-item-section>
                            <q-item-label>
                              <span class="text-weight-medium">
                                {{ getComponentName(link.from_component) }}
                              </span>
                              .{{ link.from_slot }}
                              <q-icon name="arrow_forward" size="xs" class="q-mx-xs" />
                              <span class="text-weight-medium">
                                {{ getComponentName(link.to_component) }}
                              </span>
                              .{{ link.to_slot }}
                            </q-item-label>
                          </q-item-section>
                        </q-item>
                      </q-list>
                    </q-card-section>
                  </q-card>
                </div>
              </div>
            </q-scroll-area>
          </template>
        </q-splitter>
      </div>
    </q-card>

    <!-- Canvas Preview Dialog -->
    <q-dialog v-model="showCanvasPreview" maximized>
      <q-card>
        <q-toolbar class="bg-grey-2">
          <q-toolbar-title>
            {{ selectedEquipment?.name }} - SAX Program Preview
          </q-toolbar-title>
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>

        <q-card-section class="q-pa-none">
          <SaxVisualCanvas
            v-if="selectedEquipment"
            :equipment-id="selectedEquipment.id"
            :site-id="siteId"
            :readonly="true"
          />
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { useQuasar } from 'quasar';
  import axios from 'axios';
  import SaxVisualCanvas from './SaxVisualCanvas.vue';

  // Props
  const props = defineProps({
    equipmentId: {
      type: Number,
      required: true,
    },
    siteId: {
      type: Number,
      default: null,
    },
  });

  // Quasar
  const $q = useQuasar();

  // State
  const attachedEquipment = ref([]);
  const selectedEquipment = ref(null);
  const selectedEquipmentId = ref(null);
  const selectedEquipmentSaxData = ref(null);
  const isLoading = ref(false);
  const searchQuery = ref('');
  const splitterModel = ref(40);
  const showCanvasPreview = ref(false);

  // Computed

  /**
   * Equipment tree structure
   */
  const equipmentTree = computed(() => {
    if (attachedEquipment.value.length === 0) {
      return [];
    }

    // Group by equipment type
    const grouped = {};

    attachedEquipment.value.forEach((equip) => {
      const type = equip.type?.toUpperCase() || 'OTHER';
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(equip);
    });

    // Build tree nodes
    return Object.entries(grouped).map(([type, equipment]) => ({
      id: `type-${type}`,
      label: `${type} (${equipment.length})`,
      icon: 'folder',
      color: 'amber',
      children: equipment.map((equip) => ({
        id: `equip-${equip.id}`,
        equipment_id: equip.id,
        label: equip.name,
        subtitle: equip.location,
        icon: getEquipmentIcon(equip.type),
        color: getEquipmentColor(equip.type),
        has_sax: equip.has_sax_program,
        equipment: equip,
      })),
    }));
  });

  /**
   * Unique kits in SAX data
   */
  const uniqueKits = computed(() => {
    if (!selectedEquipmentSaxData.value?.components) {
      return [];
    }

    const kits = new Set();
    selectedEquipmentSaxData.value.components.forEach((comp) => {
      if (comp.kit_name) {
        kits.add(comp.kit_name);
      }
    });

    return Array.from(kits);
  });

  // Methods

  /**
   * Load attached equipment
   */
  async function loadAttachedEquipment() {
    isLoading.value = true;
    try {
      const apiPath = props.siteId
        ? `/v2/sites/${props.siteId}/equipment/${props.equipmentId}/attached`
        : `/equipment/${props.equipmentId}/attached`;

      const response = await axios.get(apiPath);
      attachedEquipment.value = response.data.data || [];
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load attached equipment',
        caption: error.message,
      });
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Select equipment
   */
  async function selectEquipment(node) {
    if (!node.equipment_id) {return;}

    selectedEquipmentId.value = node.equipment_id;
    selectedEquipment.value = node.equipment;

    // Load SAX data for selected equipment
    if (node.has_sax) {
      await loadSaxDataForEquipment(node.equipment_id);
    } else {
      selectedEquipmentSaxData.value = null;
    }
  }

  /**
   * Load SAX data for equipment
   */
  async function loadSaxDataForEquipment(equipmentId) {
    try {
      const apiPath = props.siteId
        ? `/v2/sites/${props.siteId}/equipment/${equipmentId}/documentation/sax`
        : `/equipment/${equipmentId}/documentation/sax`;

      const response = await axios.get(apiPath);
      selectedEquipmentSaxData.value = response.data.sax_data || null;
    } catch (error) {
      console.error('Failed to load SAX data:', error);
      selectedEquipmentSaxData.value = null;
    }
  }

  /**
   * Export SAX for equipment
   */
  async function exportSaxForEquipment() {
    if (!selectedEquipment.value) {return;}

    try {
      const apiPath = props.siteId
        ? `/v2/sites/${props.siteId}/equipment/${selectedEquipment.value.id}/documentation/sax/export`
        : `/equipment/${selectedEquipment.value.id}/documentation/sax/export`;

      const response = await axios.get(apiPath, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedEquipment.value.name}_sax_${Date.now()}.sax`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      $q.notify({
        type: 'positive',
        message: 'SAX file exported',
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to export SAX file',
        caption: error.message,
      });
    }
  }

  /**
   * Open equipment editor
   */
  function openEquipmentEditor() {
    if (!selectedEquipment.value) {return;}

    // Navigate to equipment editor
    // This would depend on the routing structure of pricing-portal-laravel
    $q.notify({
      type: 'info',
      message: 'Opening equipment editor...',
      caption: 'This would navigate to the equipment editor',
    });
  }

  /**
   * Get equipment icon
   */
  function getEquipmentIcon(type) {
    const icons = {
      ahu: 'air',
      vav: 'tune',
      fcu: 'heat_pump',
      controller: 'router',
      zone: 'meeting_room',
    };

    return icons[type?.toLowerCase()] || 'devices_other';
  }

  /**
   * Get equipment color
   */
  function getEquipmentColor(type) {
    const colors = {
      ahu: 'blue',
      vav: 'green',
      fcu: 'orange',
      controller: 'purple',
      zone: 'teal',
    };

    return colors[type?.toLowerCase()] || 'grey';
  }

  /**
   * Get component name by ID
   */
  function getComponentName(componentId) {
    if (!selectedEquipmentSaxData.value?.components) {
      return componentId;
    }

    const component = selectedEquipmentSaxData.value.components.find((c) => c.id === componentId);
    return component?.name || componentId;
  }

  /**
   * Filter equipment
   */
  function filterEquipment(node, filter) {
    const filt = filter.toLowerCase();
    return node.label?.toLowerCase().includes(filt) ||
      node.subtitle?.toLowerCase().includes(filt);
  }

  /**
   * Handle equipment select from tree
   */
  function handleEquipmentSelect(target) {
  // Tree selection handler
  }

  // Lifecycle

  onMounted(() => {
    loadAttachedEquipment();
  });
</script>

<style scoped lang="scss">
.controller-readonly-view {
  height: 100%;

  .equipment-tree {
    height: calc(100vh - 200px);
  }
}

.selected-node {
  background: #e3f2fd;
  border-radius: 4px;
  padding: 4px 8px;
}

.color-preview {
  width: 30px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #ccc;
}
</style>
